// src/pages/DriverListPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Logout } from '@mui/icons-material';
import DriverList from '../components/DriverList';
import DriverDetails from '../components/DriverDetails';
import EnvironmentSwitcher from '../components/EnvironmentSwitcher';
import { getDrivers, getAccounts, getFleetToken } from '../api';

const DriverListPage = () => {
  const { fleetUid } = useParams();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedDriverStatus, setSelectedDriverStatus] = useState(null);
  const [driverStatuses, setDriverStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fleets, setFleets] = useState([]);
  const orgToken = sessionStorage.getItem('orgToken');
  const fleetToken = sessionStorage.getItem('fleetToken');
  const fleetName = sessionStorage.getItem('selectedFleetName');

  useEffect(() => {
    const fetchFleets = async () => {
      try {
        const accounts = await getAccounts(orgToken);
        const organizationAccounts = accounts.filter(account => account.type === 1);
        setFleets(organizationAccounts);
      } catch (err) {
        console.error('Error fetching fleets:', err);
      }
    };

    if (orgToken) {
      fetchFleets();
    }
  }, [orgToken]);

  useEffect(() => {
    const fetchDrivers = async () => {
    try {
      setLoading(true);
        const data = await getDrivers(fleetToken);
        setDrivers(data);
        setError(null);
    } catch (err) {
        setError('Failed to fetch drivers');
        console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
    };

    if (fleetToken) {
      fetchDrivers();
    }
  }, [fleetToken]);

  // Update selected driver status when driver is selected or statuses are updated
  useEffect(() => {
    if (selectedDriver && driverStatuses[selectedDriver.uid]) {
      setSelectedDriverStatus(driverStatuses[selectedDriver.uid]);
    } else {
      setSelectedDriverStatus(null);
    }
  }, [selectedDriver, driverStatuses]);

  const handleSelectDriver = (driver) => {
    setSelectedDriver(driver);
  };

  const handleStatusesUpdate = (statuses) => {
    setDriverStatuses(statuses);
  };

  const handleDriversUpdate = () => {
    const fetchDrivers = async () => {
      try {
        const data = await getDrivers(fleetToken);
        setDrivers(data);
      } catch (err) {
        console.error('Error refreshing drivers:', err);
      }
    };

    fetchDrivers();
  };

  const handleFleetChange = async (event, newValue) => {
    if (newValue) {
      try {
        setLoading(true);
        const newFleetToken = await getFleetToken(orgToken, newValue.uid);
        sessionStorage.setItem('fleetToken', newFleetToken);
        sessionStorage.setItem('selectedFleetName', newValue.name);
        navigate(`/fleets/${newValue.uid}/drivers`);
      } catch (err) {
        setError('Failed to switch fleet');
        console.error('Error switching fleet:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('orgToken');
    sessionStorage.removeItem('fleetToken');
    sessionStorage.removeItem('selectedFleetName');
    navigate('/');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const currentFleet = fleets.find(fleet => fleet.uid === fleetUid);

  return (
    <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        mb: 3,
        px: 2
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          width: '100%',
          maxWidth: '1200px' // Match the approximate width of the main content area
        }}>
          <Typography variant="h4">
            {fleetName ? `${fleetName} (${fleetUid})` : 'Driver List'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <EnvironmentSwitcher />
            <Autocomplete
              value={currentFleet}
              onChange={handleFleetChange}
              options={fleets}
              getOptionLabel={(option) => `${option.name} (${option.uid})`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Fleet"
                  size="small"
                  sx={{ width: 200 }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option.uid}
                    </Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.uid === value.uid}
            />
            <Tooltip title="Logout">
              <IconButton
                onClick={handleLogout}
                sx={{ color: 'text.secondary' }}
              >
                <Logout />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: 3, 
        flex: 1, 
        minHeight: 0,
        px: 2
      }}>
        <Box sx={{ 
          width: '25%', 
          minWidth: '300px',
          maxWidth: '400px',
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column' 
        }}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              backgroundColor: 'transparent',
            }}
          >
            <DriverList
              drivers={drivers}
              selectedDriver={selectedDriver}
              onSelectDriver={handleSelectDriver}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onDriversUpdate={handleDriversUpdate}
              onStatusesUpdate={handleStatusesUpdate}
            />
          </Paper>
        </Box>

        <Box sx={{ 
          width: '50%', 
          minWidth: '500px',
          maxWidth: '800px',
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column' 
        }}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              backgroundColor: 'transparent',
            }}
          >
            {selectedDriver ? (
              <DriverDetails
                driver={selectedDriver}
                driverStatus={selectedDriverStatus}
                token={fleetToken}
                onDriversUpdate={handleDriversUpdate}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Select a driver to view details
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default DriverListPage;
